/**
 * Forgot PIN Page Component
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, User, ChevronRight, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CountdownTimer from '../components/CountdownTimer';
import { API_URL, ALLOW_DEV_OTP, OTP_TIMEOUT_SECONDS } from '../config';
import { applyOtpPaste, handleOtpPasteEvent } from '../lib/otpUtils';
import AuthSplitShell from '../components/marketing/AuthSplitShell';
import { IMAGES } from '../lib/marketingImages';

const ForgotPINPage = () => {
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpTimeout, setOtpTimeout] = useState(OTP_TIMEOUT_SECONDS);
  const [otpSession, setOtpSession] = useState(0); // bump to restart the countdown
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) {
      toast.error("Please enter your email or phone number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-pin`, { identifier });
      toast.success("Reset code sent!");
      if (response.data.dev_otp) setDevOtp(response.data.dev_otp);
      if (response.data.expires_in_seconds) setOtpTimeout(response.data.expires_in_seconds);
      setOtpSession((s) => s + 1);
      setOtpExpired(false);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API_URL}/auth/resend-otp?identifier=${encodeURIComponent(identifier)}`);
      toast.success("New reset code sent!");
      if (response.data.dev_otp) setDevOtp(response.data.dev_otp);
      if (response.data.expires_in_seconds) setOtpTimeout(response.data.expires_in_seconds);
      setOtpSession((s) => s + 1);
      setOtpExpired(false);
      setOtp(["", "", "", "", "", ""]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const handleOtpExpire = () => {
    setOtpExpired(true);
    toast.error("Reset code expired! Please request a new one.");
  };

  const handleOtpChange = (index, value) => {
    const { otp: newOtpArr, focusIndex } = applyOtpPaste(otp, index, value);
    setOtp(newOtpArr);
    if (newOtpArr[index] !== otp[index] || value.replace(/\D/g, "").length > 1) {
      document.getElementById(`reset-otp-${focusIndex}`)?.focus();
    }
  };

  const handleOtpPaste = (index, e) => {
    const { otp: newOtpArr, focusIndex } = handleOtpPasteEvent(e, otp, index);
    setOtp(newOtpArr);
    document.getElementById(`reset-otp-${focusIndex}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`reset-otp-${index - 1}`)?.focus();
    }
  };

  const handleResetPIN = async (e) => {
    e.preventDefault();
    if (otpExpired) {
      toast.error("Code expired. Please request a new one.");
      return;
    }
    const code = otp.join("");
    if (code.length !== 6) { toast.error("Please enter the complete code"); return; }
    if (newPin.length < 4 || newPin.length > 6) { toast.error("PIN must be 4-6 digits"); return; }
    if (newPin !== confirmPin) { toast.error("PINs do not match"); return; }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/reset-pin`, { identifier, code, new_pin: newPin });
      login(response.data.access_token, response.data.user);
      toast.success("PIN reset successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitShell
      image={IMAGES.kitchen}
      kicker="We've got you"
      headline="Misplaced your PIN? Let's get you back to paying bills."
      caption="A short code to your email, a new PIN, and you're home again."
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground">Reset your PIN</h1>
          <p className="text-muted-foreground mt-2">We'll help you back into your home account</p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-xl">
          {step === 1 ? (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-muted-foreground">Email or Phone Number</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="Enter your email or phone" className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    data-testid="forgot-identifier-input" />
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full py-4 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg" data-testid="send-reset-code-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Reset Code <ChevronRight className="w-5 h-5" /></>}
              </button>

              <button type="button" onClick={() => navigate("/login")} className="w-full py-3 text-muted-foreground hover:text-foreground transition-all flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPIN} className="space-y-5">
              <button type="button" onClick={() => setStep(1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="p-4 bg-muted/50 rounded-xl">
                <CountdownTimer key={otpSession} seconds={otpTimeout} onExpire={handleOtpExpire} label="Code expires in" />
              </div>

              {ALLOW_DEV_OTP && devOtp && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <p className="text-xs text-amber-600 dark:text-amber-400 text-center">Dev Mode - Reset Code: <span className="font-mono font-bold">{devOtp}</span></p>
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">Enter the 6-digit code sent to {identifier}</p>
              </div>

              <div className="flex justify-center gap-2">
                {otp.map((digit, i) => (
                  <input key={i} id={`reset-otp-${i}`} type="text" inputMode="numeric" maxLength={6} value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value.replace(/\D/g, ""))}
                    onPaste={(e) => handleOtpPaste(i, e)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold bg-input border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={otpExpired}
                    data-testid={`reset-otp-input-${i}`} />
                ))}
              </div>

              {otpExpired && (
                <div className="text-center">
                  <p className="text-sm text-red-500 mb-2">Code expired!</p>
                  <button type="button" onClick={handleResendCode} disabled={resending}
                    className="text-orange-500 hover:underline flex items-center justify-center gap-2 mx-auto">
                    {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Resend Code
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-muted-foreground">New PIN</label>
                  <input type="password" inputMode="numeric" maxLength={6} value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="4-6 digits" className="w-full px-4 py-4 bg-input/50 border border-border rounded-xl tracking-widest"
                    data-testid="new-pin-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-muted-foreground">Confirm PIN</label>
                  <input type="password" inputMode="numeric" maxLength={6} value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="Repeat PIN" className="w-full px-4 py-4 bg-input/50 border border-border rounded-xl tracking-widest"
                    data-testid="confirm-new-pin-input" />
                </div>
              </div>

              <button type="submit" disabled={loading || otpExpired} className="w-full py-4 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg" data-testid="reset-pin-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Reset PIN <ChevronRight className="w-5 h-5" /></>}
              </button>

              {!otpExpired && (
                <button type="button" onClick={handleResendCode} disabled={resending}
                  className="w-full py-2 text-muted-foreground hover:text-foreground flex items-center justify-center gap-2">
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Didn't receive code? Resend
                </button>
              )}
            </form>
          )}
        </div>
      </motion.div>
    </AuthSplitShell>
  );
};

export default ForgotPINPage;